/**
 * Central logging utility.
 *
 * Outside DEV only `error` reaches the console; everything else is a no-op.
 * `debugStart`/`debugEnd` are a matched pair for timing a block of work: call
 * `debugStart(label)` before it and `debugEnd(label)` after, and the elapsed
 * time is appended to the end message.
 */

const isDev = import.meta.env.DEV
const LOGIN_PREFIX = '[timing]'

const timers = new Map<string, number>()

function timestamp () {
    return `[t=${performance.now().toFixed(1)}ms]`
}

function debug (...args: unknown[]) {
    if (isDev) {
        console.debug(timestamp(), ...args)
    }
}

function log (...args: unknown[]) {
    if (isDev) {
        console.log(timestamp(), ...args)
    }
}

function warn (...args: unknown[]) {
    if (isDev) {
        console.warn(timestamp(), ...args)
    }
}

function error (...args: unknown[]) {
    console.error(timestamp(), ...args)
}

function debugStart (message: string, ...args: unknown[]) {
    timers.set(message, performance.now())
    if (isDev) {
        console.log(`START: ${message}`, ...args) // ${timestamp()}
    }
}

function debugEnd (message: string, ...args: unknown[]) {
    const start = timers.get(message)
    timers.delete(message)
    const elapsed = start === undefined ? 'unknown' : `${(performance.now() - start).toFixed(1)}ms`

    if (isDev) {
        console.log(`END: [d: ${elapsed}] ${message}`, ...args) // ${timestamp()}
    }
}

export function loginMark (label: string) {
    if (isDev) {
        console.info(`${label}`) // ${LOGIN_PREFIX} ${timestamp()}
    }
}

export async function loginTime<T> (label: string, work: () => Promise<T>): Promise<T> {
    const start = performance.now()
    try {
        return await work()
    } finally {
        if (isDev) {
            console.info(`[d=${(performance.now() - start).toFixed(0)}ms] ${label}`) // ${timestamp()} ${LOGIN_PREFIX}
        }
    }
}

export const logger = {
    debug,
    log,
    warn,
    error,
    debugStart,
    debugEnd,
}
